import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { AlloyButton } from 'cat-ecommerce-alloy';
import UnitOfMeasureToggle from '../UnitOfMeasureToggle';
import { UOM } from '../../../constants/commonConstants';
import { isEmpty, isMobileDevice } from '../../../utils';
import useAnalytics from '../../../hooks/useAnalytics';
import usePagination from '../../pages/plp/usePagination';
import BucketModelNumber from '../../pages/plp/ProductListSpecifications/BucketModelNumber';
import SpecificationFacets from './SpecificationFacets';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

const ProductListSpecifications = ({
  customspecificationFacets = null,
  className,
  isModal,
  showSpecificationsToggle,
  bucketModelNumberFacet,
  facets,
  unitOfMeasure,
  activeFacets,
  expandedId,
  queryActionParam,
  productCardExpanded,
  widgetId,
  queryParamsObject,
  filterFacetsByUOM,
  orderBy,
  fbeEnabled
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { dispatchWithImpressionEvent } = useAnalytics();
  const { setUrlToPageOneWithFilter } = usePagination();
  const isMobiledevice = isMobileDevice();

  const closeExpandedProductCard = () => {
    if (expandedId !== null) {
      dispatch(productCardExpanded(null, widgetId));
    }
  };
  const { hash } = useLocation();
  const { facets: facetsFromHash } = queryString.parse(hash);
  const facetsArray = !Array.isArray(facetsFromHash)
    ? [facetsFromHash]
    : facetsFromHash;

  const isBucketGetSubcategory = !!bucketModelNumberFacet;

  const handleUnitOfMeasureChange = uomValue => {
    const facetsWithoutUoM = activeFacets?.filter(
      ({ uom }) => uom === UOM.null || uom === UOM.c62
    );
    const isSameActiveFacets =
      activeFacets?.length === facetsWithoutUoM?.length &&
      !widgetId &&
      activeFacets?.every((val, index) => val === facetsWithoutUoM[index]);
    const updatedQuerysObject = {
      unitOfMeasure: uomValue,
      activeFacets: facetsWithoutUoM
    };
    if (!isEmpty(queryParamsObject)) {
      updatedQuerysObject.queryParamsObject = queryParamsObject;
      updatedQuerysObject.sortBy = orderBy;
    }
    const actionToDispatch = fetchPlpDetails =>
      queryActionParam(
        widgetId
          ? {
              widgetId: widgetId,
              data: {
                unitOfMeasure: uomValue,
                batchPartNumbers: [],
                pageNumber: 1,
                activeFacets: facetsWithoutUoM
              }
            }
          : updatedQuerysObject,
        fetchPlpDetails
      );
    isSameActiveFacets && !widgetId
      ? dispatch(actionToDispatch(false))
      : dispatchWithImpressionEvent(actionToDispatch(true));
    !!filterFacetsByUOM && dispatch(filterFacetsByUOM(uomValue));

    !widgetId &&
      !queryParamsObject &&
      setUrlToPageOneWithFilter({ facets: facetsWithoutUoM });
  };

  const getFilteredFacets = () => {
    if (showSpecificationsToggle) {
      const unit = unitOfMeasure === UOM.us ? UOM.metric : UOM.us;
      return facets.filter(a => a.uom !== unit);
    }
    return facets;
  };

  const clearHandleClick = () => {
    const {
      orderBy: orderByFromUrl,
      filterByEquipment: filterByEquipmentFromUrl
    } = queryString.parse(hash);
    dispatch(
      queryActionParam(
        widgetId
          ? {
              widgetId: widgetId,
              data: {
                activeFacets: [],
                pageNumber: 1,
                batchPartNumbers: []
              }
            }
          : { activeFacets: [] },
        true
      )
    );
    const orderByKey = orderByFromUrl ? 'orderBy' : 'sortBy';
    if (widgetId) {
      let activeFacetsWithWidgetId = [];
      activeFacets.forEach(j => {
        activeFacetsWithWidgetId.push(`${j.facet}|${widgetId}`);
      });

      const finalUrlFacets = facetsArray
        .filter(facet => !activeFacetsWithWidgetId.includes(facet))
        .map(item => ({ facet: item }));
      closeExpandedProductCard();
      setUrlToPageOneWithFilter({
        filterByEquipment: filterByEquipmentFromUrl,
        facets: finalUrlFacets,
        [orderByKey]: orderByFromUrl,
        isMarketingPage: true,
        fbeEnabledValue: fbeEnabled
      });
    } else {
      closeExpandedProductCard();
      setUrlToPageOneWithFilter({
        filterByEquipment: filterByEquipmentFromUrl,
        facets: [],
        [orderByKey]: orderByFromUrl
      });
    }
  };
  return (
    <section className={cx(className)} data-testid="ProductListSpecifications">
      {!isEmpty(facets) ? (
        <>
          <h3 className="h3 pt-2 text-xl text-break u-color--ExtraDarkGray">
            {t('SPECIFICATIONS')}
          </h3>
          <UnitOfMeasureToggle
            name="desktop"
            unitOfMeasure={unitOfMeasure}
            setUnitOfMeasure={handleUnitOfMeasureChange}
            showLabel
            showToggle={showSpecificationsToggle}
            widgetId={widgetId}
          />
          {!isEmpty(activeFacets) && !isModal && !isMobiledevice && (
            <AlloyButton
              buttonType="link"
              onClick={clearHandleClick}
              className="ps-0"
            >
              {t('CLEAR_ALL_FILTERS')}
            </AlloyButton>
          )}
          {isBucketGetSubcategory && <BucketModelNumber isModal={isModal} />}
          <hr />
          {customspecificationFacets ?? (
            <SpecificationFacets
              facets={getFilteredFacets()}
              isModal={isModal}
              activeFacets={activeFacets}
              queryActionParam={queryActionParam}
              widgetId={widgetId}
              fbeEnabled={fbeEnabled}
            />
          )}
        </>
      ) : null}
    </section>
  );
};

export default ProductListSpecifications;

ProductListSpecifications.propTypes = {
  className: PropTypes.string,
  isModal: PropTypes.bool,
  showSpecificationsToggle: PropTypes.bool,
  unitOfMeasure: PropTypes.string,
  facets: PropTypes.arrayOf(
    PropTypes.shape({
      allValuesReturned: PropTypes.bool,
      allowMultipleValueSelection: PropTypes.bool,
      facetId: PropTypes.string,
      label: PropTypes.string,
      uom: PropTypes.string,
      value: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          facet: PropTypes.string,
          label: PropTypes.string
        })
      )
    })
  ).isRequired,
  activeFacets: PropTypes.arrayOf(
    PropTypes.shape({
      facet: PropTypes.string,
      label: PropTypes.string,
      uom: PropTypes.string
    })
  ),
  expandedId: PropTypes.string,
  queryActionParam: PropTypes.func,
  productCardExpanded: PropTypes.func,
  fbeEnabled: PropTypes.bool,
  widgetId: PropTypes.string,
  filterFacetsByUOM: PropTypes.func,
  orderBy: PropTypes.string,
  bucketModelNumberFacet: PropTypes.bool,
  customspecificationFacets: PropTypes.element,
  queryParamsObject: PropTypes.objectOf(
    PropTypes.shape({
      history: PropTypes.objectOf(
        PropTypes.shape({ push: PropTypes.func, back: PropTypes.func })
      ),
      url: PropTypes.string,
      hash: PropTypes.string,
      search: PropTypes.string
    })
  )
};
